<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { BaseAvatar, BaseIcon, BaseLoader } from '@magma-app/magma-lapilli';
import TheFooter from '@/components/shell/TheFooter.vue';
import SignupIntroStep from './steps/SignupIntroStep.vue';
import ProfileStep from './steps/ProfileStep.vue';
import PhoneVerificationStep from './steps/PhoneVerificationStep.vue';
import OptionalCriteriaStep from './steps/OptionalCriteriaStep.vue';
import RedirectionStep from './steps/RedirectionStep.vue';

import { useConvertHexToRgb } from '@/composables/useConvertHexToRgb';
import { useAuthStore } from '@/stores/auth';
import { useSignupSteps } from '@/composables/helpee-signup/useSignupSteps';
import { usePhoneVerification } from '@/composables/helpee-signup/usePhoneVerification';
import { useHelpeeHelperPictures } from './composables/useHelpeeHelperPictures';
import { useHelpeeSignupBootstrap } from './composables/useHelpeeSignupBootstrap';
import { useHelpeeSignupFlow } from './composables/useHelpeeSignupFlow';
import { useHelpeeSignupViewModel } from './composables/useHelpeeSignupViewModel';

const route = useRoute();
const router = useRouter();
const { locale } = useI18n({ useScope: 'global' });
const authStore = useAuthStore();
const { hexColor, rgbColor } = useConvertHexToRgb();

const steps = useSignupSteps();
const phoneVerification = usePhoneVerification();

const viewModel = useHelpeeSignupViewModel({
  authStore,
  isIntro: steps.isIntro,
});

const flow = useHelpeeSignupFlow({
  authStore,
  route,
  steps,
  phoneVerification,
  strongCriteria: viewModel.strongCriteria,
  optionalCriteria: viewModel.optionalCriteria,
});

const bootstrap = useHelpeeSignupBootstrap({
  route,
  router,
  authStore,
  locale,
  hexColor,
  isIntro: steps.isIntro,
  goToStep: steps.goToStep,
  profileForm: flow.profileForm,
});

const pictures = useHelpeeHelperPictures({
  helpeeSignUpData: computed(() => authStore.helpeeSignUpData),
});

const { currentStep, isIntro, isPhone, isRedirect } = steps;
const { codeError, timerBeforeResend, canResend, isResending } =
  phoneVerification;

const {
  isLoading,
  phoneError,
  clearPhoneError,
  profileForm,
  verificationCode,
  optionalCriteriaAnswers,
  phoneRequired,
  hasOptionalSelection,
  handleIntroSuccess,
  handleProfileSubmit,
  handleResendCode,
  handleValidateCode,
  handleOptionalCriteriaSubmit,
  handleStrongCriteriaChange,
  handleOptionalCriteriaChange,
  updateVerificationCode,
  updateOptionalCriteriaAnswers,
  goBack,
  goBackToProfile,
} = flow;

const { initialized, isWidget, containerWidth, flowContainerRef } = bootstrap;
const {
  strongCriteria,
  optionalCriteria,
  strongCriteriaOptions,
  backgroundClass,
  isInFlow,
} = viewModel;

const helpersProfilePicture = pictures.redirectHelpersProfilePicture;
const shouldDisplayPhoneStep = computed(
  () => isPhone.value || authStore.helpeePostMatchData?.needPhoneNumberVerification !== false
);

const displayedFlowSteps = computed(() => {
  const steps: Array<'profile' | 'phone' | 'criteria'> = ['profile'];

  if (shouldDisplayPhoneStep.value) {
    steps.push('phone');
  }

  if (optionalCriteria.value.length > 0) {
    steps.push('criteria');
  }

  return steps;
});

const currentFlowStepNumber = computed(() => {
  const stepIndex = displayedFlowSteps.value.findIndex(
    (step) => step === currentStep.value
  );

  return stepIndex >= 0 ? stepIndex + 1 : 1;
});

const totalFlowSteps = computed(() => displayedFlowSteps.value.length);

onMounted(async () => {
  await bootstrap.mount();
});

onBeforeUnmount(() => {
  bootstrap.unmount();
});

watch(
  () => currentStep.value,
  async () => {
    if (isInFlow.value) {
      await bootstrap.computeContainerWidth();
    }
  }
);
</script>

<template>
  <div data-cy="e2e-main-div" class="flex flex-col">
    <div
      v-if="initialized"
      class="scrollbar-hide flex flex-1 flex-col"
      :class="[
        backgroundClass,
        isWidget
          ? 'relative shadow-[0px_20px_24px_-4px_rgba(28,31,39,0.08),0px_8px_8px_-4px_rgba(28,31,39,0.03)]'
          : 'mx-auto w-full md:relative md:mt-20 md:h-[650px] md:w-[654px] md:rounded-[15px] md:shadow-[0px_20px_24px_-4px_rgba(28,31,39,0.08),0px_8px_8px_-4px_rgba(28,31,39,0.03)]',
        !isWidget && isInFlow
          ? 'overflow-y-scroll md:max-h-[650px]'
          : !isWidget
            ? 'max-h-[650px]'
            : undefined,
        {
          'h-full': !isInFlow
        }
      ]"
    >
      <div
        :class="[
          'flex h-16 items-center p-4 md:px-10',
          {
            'border-b border-b-border-subtle':
              isInFlow || authStore.helpeeSignUpData?.headerType === 'image'
          }
        ]"
      >
        <BaseAvatar
          :src="authStore.helpeeSignUpData?.logoUrl"
          :alt="'logo ' + authStore.helpeeSignUpData?.organizationName"
          :fallback="authStore.helpeeSignUpData?.name"
        />

        <h1
          class="max-w-[300px] truncate px-2 text-mgm-txt-sm font-medium text-foreground-emphasis lg:max-w-[300px]"
        >
          {{ authStore.helpeeSignUpData?.name }}
        </h1>
      </div>

      <SignupIntroStep
        v-if="isIntro"
        :is-widget="isWidget"
        @success="handleIntroSuccess"
      />

      <div
        v-else
        ref="flowContainerRef"
        class="js-container flex flex-1 flex-col"
        :class="[
          isRedirect
            ? 'md:pb-6'
            : isWidget
              ? containerWidth < 600
                ? 'px-6 pb-6 pt-6'
                : 'px-10 pb-16 pt-6'
              : 'px-6 pt-6 pb-6 md:px-10'
        ]"
      >
        <div v-if="!isRedirect" class="mb-4 flex">
          <BaseIcon color="#5F6C85" icon="chevron-left" @click="goBack" />

          <span
            class="ml-2 block pr-1 text-mgm-txt-xs font-medium text-foreground-emphasis"
          >
            {{ $t('generic.step') + ' ' + currentFlowStepNumber }}
          </span>
          <span
            class="block text-mgm-txt-xs font-medium text-foreground-subtle"
          >
            {{ `/ ${totalFlowSteps}` }}
          </span>
        </div>

        <ProfileStep
          v-if="currentStep === 'profile'"
          :strong-criteria="strongCriteria"
          :criteria-options="strongCriteriaOptions"
          :initial-values="profileForm"
          :phone-required="phoneRequired"
          :primary-color="authStore.helpeeSignUpData?.primaryColor"
          :rgb-color="rgbColor || authStore.helpeeSignUpData?.primaryColor"
          :is-loading="isLoading"
          :phone-error="phoneError"
          @submit="handleProfileSubmit"
          @criteria-change="handleStrongCriteriaChange"
          @phone-change="clearPhoneError"
        />

        <PhoneVerificationStep
          v-else-if="currentStep === 'phone'"
          :phone-number="profileForm.phoneNumber"
          :code="verificationCode"
          :code-error="codeError"
          :can-resend="canResend"
          :timer-before-resend="timerBeforeResend"
          :is-resending="isResending"
          :is-loading="isLoading"
          :primary-color="authStore.helpeeSignUpData?.primaryColor"
          :rgb-color="rgbColor || authStore.helpeeSignUpData?.primaryColor"
          @update:code="updateVerificationCode"
          @resend="handleResendCode"
          @submit="handleValidateCode"
          @back="goBackToProfile"
        />

        <OptionalCriteriaStep
          v-else-if="currentStep === 'criteria'"
          :criteria-list="optionalCriteria"
          :answers="optionalCriteriaAnswers"
          :can-submit="hasOptionalSelection"
          :is-loading="isLoading"
          :primary-color="authStore.helpeeSignUpData?.primaryColor"
          :rgb-color="rgbColor || authStore.helpeeSignUpData?.primaryColor"
          @update:answers="updateOptionalCriteriaAnswers"
          @criteria-change="handleOptionalCriteriaChange"
          @submit="handleOptionalCriteriaSubmit"
        />

        <RedirectionStep
          v-else-if="currentStep === 'redirect'"
          :helpers="helpersProfilePicture"
          :container-width="containerWidth"
          :redirect-url="
            authStore.helpeePostCompleteSignUpData?.prospectSignupRedirectUrl
          "
        />
      </div>

      <TheFooter
        class="bg-background-default md:sticky md:bottom-0 md:left-0 md:right-0 md:mt-0"
        :class="{
          'md:static': !isWidget && isInFlow
        }"
      />
    </div>

    <BaseLoader v-else class="min-h-screen" />
  </div>
</template>

<style scoped>
.gradient__community {
  background: linear-gradient(180deg, rgba(68, 76, 231, 0.15) 0%, #ffffff 40%);
}
</style>
